'use client'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Always return string at the hook boundary. i18next-icu's type augmentation
// widens t()'s return to include TFunctionDetailedResult under some inference
// paths; we never rely on details, so the cast is safe and keeps callers from
// having to .toString() everything.
export type TranslateFn = (key: string, values?: Record<string, unknown>) => string

// Drop-in for next-intl's useTranslations(namespace). Returns react-i18next's
// own `t` (with keyPrefix), so the identity is stable across renders — passing
// it through useCallback/useEffect deps no longer fires on every render.
export function useTranslations(namespace?: string): TranslateFn {
  const { t } = useTranslation(undefined, namespace ? { keyPrefix: namespace } : undefined)
  return t as unknown as TranslateFn
}

// next-intl-style `t` with `.has(key)` and `.raw(key)` attached. Used where a
// caller needs to test for a missing translation or pull the raw resource
// (e.g. structured solution copy stored as nested JSON under one key).
export function useTranslationsExt(namespace: string) {
  const { t: tRoot, i18n } = useTranslation()
  return useMemo(() => {
    const prefix = `${namespace}.`
    const fn: TranslateFn = (key, values) =>
      tRoot(prefix + key, values as never) as unknown as string
    return Object.assign(fn, {
      has: (key: string) => i18n.exists(prefix + key),
      raw: (key: string) => i18n.getResource(i18n.language, 'translation', prefix + key),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, tRoot, i18n, i18n.language])
}
